// ---------------------------------------------------------------------------
// Lightweight offline cache for the last successful trip + its scored routes.
// ---------------------------------------------------------------------------
// Strategy: stash a structured snapshot in localStorage whenever the user
// gets a fresh set of routes. When the device goes offline (or a cold load
// happens with no network), the app can restore the snapshot read-only so
// users still see their last directions instead of an empty screen.
//
// We deliberately avoid persisting Map/Set/functions/refs — only plain JSON
// the UI needs to render the route list and basic context.
// ---------------------------------------------------------------------------

import type { ClassifiedRoute } from "@/utils/scoring";

const KEY = "pleasant.offlineTrip.v1";
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

export interface OfflineTripSnapshot {
  /** ISO timestamp of when the snapshot was saved. */
  savedAt: string;
  /** Display labels — kept tiny on purpose. */
  startLabel?: string;
  destLabel?: string;
  startCoords?: [number, number];
  destCoords?: [number, number];
  mode?: string;
  /** Scored routes — full enough for the route cards + map polylines. */
  scored: ClassifiedRoute[];
}

export function saveOfflineTrip(snap: OfflineTripSnapshot): void {
  try {
    const payload = JSON.stringify(snap);
    // Hard cap — localStorage explodes around 5MB on most browsers.
    if (payload.length > 1_500_000) return;
    localStorage.setItem(KEY, payload);
  } catch {
    // Quota / private mode — silently ignore, the cache is best-effort.
  }
}

export function loadOfflineTrip(): OfflineTripSnapshot | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as OfflineTripSnapshot;
    if (!snap?.savedAt || !Array.isArray(snap.scored) || snap.scored.length === 0) {
      return null;
    }
    const age = Date.now() - new Date(snap.savedAt).getTime();
    if (Number.isFinite(age) && age > MAX_AGE_MS) return null;
    return snap;
  } catch {
    return null;
  }
}

export function clearOfflineTrip(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Active-trip marker — distinct from the snapshot above. The snapshot is
// "your last routes, kept for offline viewing." This marker answers a
// different question: "was a trip actually on screen when the app last
// stopped?" iOS can silently kill and relaunch the app under memory
// pressure; on a cold load that otherwise lands on a blank no-destination
// screen and the in-progress drive is lost. Real tester report: "I
// completely timed out in the middle of my trip and went back to no
// destination." The marker lets a cold load notice an interrupted trip and
// restore it.
// ---------------------------------------------------------------------------

const ACTIVE_KEY = "pleasant.activeTrip.v1";

/** Record that a trip is currently on screen. Stores the time so a cold
 *  load can tell an interrupted drive from a stale one. */
export function markActiveTrip(): void {
  try {
    localStorage.setItem(ACTIVE_KEY, new Date().toISOString());
  } catch {
    /* ignore */
  }
}

/** Clear the marker — called on a clean trip exit (the user tapped back to
 *  start a new search, or arrived at the destination). */
export function clearActiveTrip(): void {
  try {
    localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* ignore */
  }
}

/** True when a trip was active recently enough to treat a cold load as an
 *  interrupted drive rather than a fresh start. `maxAgeMs` defaults to 2 h —
 *  long enough to cover a drive, short enough that yesterday's trip doesn't
 *  resurface uninvited. */
export function wasTripInterrupted(maxAgeMs = 2 * 60 * 60 * 1000): boolean {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return false;
    const age = Date.now() - new Date(raw).getTime();
    return Number.isFinite(age) && age >= 0 && age < maxAgeMs;
  } catch {
    return false;
  }
}

/** Friendly relative-time label, e.g. "saved 12 min ago". */
export function offlineTripAgeLabel(snap: OfflineTripSnapshot): string {
  const ms = Date.now() - new Date(snap.savedAt).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return "saved just now";
  if (min < 60) return `saved ${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `saved ${hr} hr ago`;
  const day = Math.round(hr / 24);
  return `saved ${day} day${day === 1 ? "" : "s"} ago`;
}

// ---------------------------------------------------------------------------
// Export / import — let users move their cached trip between devices, back
// it up before clearing browser data, or share with a friend.
// ---------------------------------------------------------------------------

const FILE_VERSION = 1;
const FILE_KIND = "pleasant.offlineTrip";

interface ExportEnvelope {
  kind: typeof FILE_KIND;
  version: number;
  exportedAt: string;
  snapshot: OfflineTripSnapshot;
}

/** Build a friendly default filename like `pleasant-trip-soho-to-park.json`. */
const slugify = (s: string | undefined): string =>
  (s ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);

export function offlineTripExportFilename(snap: OfflineTripSnapshot): string {
  const start = slugify(snap.startLabel) || "start";
  const dest = slugify(snap.destLabel) || "destination";
  return `pleasant-trip-${start}-to-${dest}.json`;
}

/** Trigger a browser download of the snapshot as JSON. */
export function downloadOfflineTrip(snap: OfflineTripSnapshot): void {
  const envelope: ExportEnvelope = {
    kind: FILE_KIND,
    version: FILE_VERSION,
    exportedAt: new Date().toISOString(),
    snapshot: snap,
  };
  const blob = new Blob([JSON.stringify(envelope, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = offlineTripExportFilename(snap);
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so Safari/iOS has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Parse + validate a previously-exported snapshot file. Throws on bad input. */
export async function parseOfflineTripFile(file: File): Promise<OfflineTripSnapshot> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("That file isn't valid JSON.");
  }
  // Accept either the wrapped envelope OR a bare snapshot (older exports).
  const candidate =
    parsed && typeof parsed === "object" && "snapshot" in (parsed as Record<string, unknown>)
      ? (parsed as ExportEnvelope).snapshot
      : (parsed as OfflineTripSnapshot);

  if (
    !candidate ||
    typeof candidate !== "object" ||
    typeof (candidate as OfflineTripSnapshot).savedAt !== "string" ||
    !Array.isArray((candidate as OfflineTripSnapshot).scored) ||
    (candidate as OfflineTripSnapshot).scored.length === 0
  ) {
    throw new Error("This doesn't look like a Pleasant trip export.");
  }
  return candidate as OfflineTripSnapshot;
}

